import React, {useState, useEffect} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import axios from 'axios';

const {FiList, FiPlus, FiEdit, FiSave, FiX, FiTrash2, FiDollarSign, FiTag, FiAward, FiToggleRight, FiToggleLeft, FiSearch, FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight} = FiIcons;

const TreatmentCodes = () => {
  const [loading, setLoading] = useState(true);
  const [allTreatmentCodes, setAllTreatmentCodes] = useState([]);
  const [treatmentCodes, setTreatmentCodes] = useState([]);
  const [filteredTreatmentCodes, setFilteredTreatmentCodes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    default_cost: 0,
    is_specialist: false,
    active: true
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 15,
    totalItems: 0
  });

  useEffect(() => {
    fetchTreatmentCodes();
  }, []);

  // Effect for search filtering
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredTreatmentCodes(allTreatmentCodes);
    } else {
      const searchTermLower = searchTerm.toLowerCase();
      const filtered = allTreatmentCodes.filter(code => 
        code.code.toLowerCase().includes(searchTermLower) ||
        code.description.toLowerCase().includes(searchTermLower)
      );
      setFilteredTreatmentCodes(filtered);
      // Reset to first page when searching
      setPagination(prev => ({
        ...prev,
        currentPage: 1
      }));
    }
  }, [searchTerm, allTreatmentCodes]);

  // Effect for pagination
  useEffect(() => {
    updatePaginatedData();
  }, [pagination.currentPage, filteredTreatmentCodes]);

  const updatePaginatedData = () => {
    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const endIndex = startIndex + pagination.itemsPerPage;
    const paginatedCodes = filteredTreatmentCodes.slice(startIndex, endIndex);
    
    setTreatmentCodes(paginatedCodes);
    
    // Update pagination info
    const totalItems = filteredTreatmentCodes.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pagination.itemsPerPage));
    
    setPagination(prev => ({
      ...prev,
      totalPages,
      totalItems,
      currentPage: prev.currentPage > totalPages ? 1 : prev.currentPage
    }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({
        ...prev,
        currentPage: newPage
      }));
      // Scroll to top when page changes
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const fetchTreatmentCodes = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/settings/treatment-codes');
      setAllTreatmentCodes(response.data);
      setFilteredTreatmentCodes(response.data);
    } catch (error) {
      console.error('Error fetching treatment codes:', error);
      setError('Failed to load treatment codes');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const {name, value, type, checked} = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const openAddModal = () => {
    setEditingCode(null);
    setFormData({
      code: '',
      description: '',
      default_cost: 0,
      is_specialist: false,
      active: true
    });
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const openEditModal = (code) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      description: code.description,
      default_cost: code.default_cost,
      is_specialist: code.is_specialist,
      active: code.active
    });
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (editingCode) {
        // Update existing code
        await axios.put(`/api/settings/treatment-codes/${editingCode.id}`, formData);
        setSuccess('Treatment code updated successfully');
      } else {
        // Create new code
        await axios.post('/api/settings/treatment-codes', formData);
        setSuccess('Treatment code created successfully');
      }

      // Refresh treatment code list
      fetchTreatmentCodes();

      // Close modal after a short delay
      setTimeout(() => {
        setShowModal(false);
      }, 1500);
    } catch (error) {
      console.error('Error saving treatment code:', error);
      setError(error.response?.data?.error || 'Failed to save treatment code');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this treatment code?')) {
      return;
    }

    try {
      const response = await axios.delete(`/api/settings/treatment-codes/${id}`);
      if (response.data.deactivated) {
        alert('This code is in use and has been deactivated instead of deleted.');
      }
      fetchTreatmentCodes();
    } catch (error) {
      console.error('Error deleting treatment code:', error);
      alert('Failed to delete treatment code: ' + (error.response?.data?.error || error.message));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-maroon-600"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{opacity: 0}}
      animate={{opacity: 1}}
      transition={{duration: 0.3}}
    >
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-grey-900">Treatment Codes</h2>
            <p className="mt-1 text-sm text-grey-600">
              Manage standardized treatment codes and their default costs. Multiple descriptions allowed per code.
            </p>
          </div>
          <button
            onClick={openAddModal}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-maroon-600 hover:bg-maroon-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-maroon-500"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
            Add Treatment Code
          </button>
        </div>

        {/* Search Bar */}
        <div className="bg-white shadow rounded-lg p-4">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1 relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-grey-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search treatment codes by code or description..."
                className="w-full pl-10 pr-10 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-grey-400 hover:text-grey-600"
                  onClick={clearSearch}
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              )}
            </div>
            <div className="text-sm text-grey-600">
              {pagination.totalItems} treatment code{pagination.totalItems !== 1 ? 's' : ''} found
            </div>
          </div>
        </div>

        {/* Treatment Codes Table */}
        <div className="bg-white shadow overflow-hidden rounded-md">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-grey-200">
              <thead className="bg-grey-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                    Code
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                    Default Cost
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-grey-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-grey-200">
                {treatmentCodes.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <SafeIcon icon={FiList} className="mx-auto h-12 w-12 text-grey-400" />
                      <h3 className="mt-2 text-sm font-medium text-grey-900">
                        {searchTerm ? 'No treatment codes found' : 'No treatment codes found'}
                      </h3>
                      <p className="mt-1 text-sm text-grey-500">
                        {searchTerm 
                          ? 'Try adjusting your search criteria.' 
                          : 'Get started by adding a new treatment code.'
                        }
                      </p>
                    </td>
                  </tr>
                ) : (
                  treatmentCodes.map((code) => (
                    <tr key={code.id} className={!code.active ? 'bg-grey-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-grey-900">
                        <div className="flex items-center">
                          <SafeIcon icon={FiTag} className="flex-shrink-0 mr-2 h-5 w-5 text-grey-400" />
                          <span>{code.code}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-grey-500">
                        <div className="max-w-xs">
                          {code.description}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-grey-500">
                        <div className="flex items-center">
                          <SafeIcon icon={FiDollarSign} className="flex-shrink-0 mr-1 h-4 w-4 text-grey-400" />
                          <span>{code.default_cost}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {code.is_specialist ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            <SafeIcon icon={FiAward} className="w-3 h-3 mr-1" />
                            Specialist
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-grey-100 text-grey-800">
                            General
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {code.active ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <SafeIcon icon={FiToggleRight} className="w-3 h-3 mr-1" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <SafeIcon icon={FiToggleLeft} className="w-3 h-3 mr-1" />
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-3">
                          <button
                            onClick={() => openEditModal(code)}
                            className="text-maroon-600 hover:text-maroon-900"
                            title="Edit"
                          >
                            <SafeIcon icon={FiEdit} className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(code.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {pagination.totalPages > 1 && (
            <div className="px-6 py-4 bg-grey-50 border-t border-grey-200 flex flex-wrap items-center justify-between">
              <div className="text-sm text-grey-700">
                Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to{' '}
                {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of{' '}
                {pagination.totalItems} treatment codes
              </div>
              <div className="flex space-x-2 mt-2 sm:mt-0">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={pagination.currentPage === 1}
                  className="p-2 rounded-md border border-grey-300 bg-white text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="First Page"
                >
                  <SafeIcon icon={FiChevronsLeft} className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className="p-2 rounded-md border border-grey-300 bg-white text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Previous Page"
                >
                  <SafeIcon icon={FiChevronLeft} className="w-5 h-5" />
                </button>
                <div className="flex items-center px-4">
                  <span className="text-grey-700">
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </span>
                </div>
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="p-2 rounded-md border border-grey-300 bg-white text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Next Page"
                >
                  <SafeIcon icon={FiChevronRight} className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handlePageChange(pagination.totalPages)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="p-2 rounded-md border border-grey-300 bg-white text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Last Page"
                >
                  <SafeIcon icon={FiChevronsRight} className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Add/Edit Treatment Code Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div
              initial={{scale: 0.95, opacity: 0}}
              animate={{scale: 1, opacity: 1}}
              className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-grey-900">
                  {editingCode ? 'Edit Treatment Code' : 'Add Treatment Code'}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-grey-400 hover:text-grey-500"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>

              {error && (
                <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4 rounded">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <SafeIcon icon={FiIcons.FiAlertTriangle} className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {success && (
                <div className="mb-4 bg-green-50 border-l-4 border-green-500 p-4 rounded">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <SafeIcon icon={FiIcons.FiCheckCircle} className="h-5 w-5 text-green-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-green-700">{success}</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-grey-700">
                    Treatment Code
                  </label>
                  <input
                    type="text"
                    id="code"
                    name="code"
                    required
                    className="mt-1 block w-full border-grey-300 rounded-md shadow-sm focus:ring-maroon-500 focus:border-maroon-500 sm:text-sm"
                    value={formData.code}
                    onChange={handleInputChange}
                    placeholder="e.g. CLN-001, SPC-ORT-001"
                  />
                  <p className="mt-1 text-xs text-grey-500">
                    Multiple descriptions can use the same code. Prefix specialist codes with "SPC-" for automatic detection.
                  </p>
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-grey-700">
                    Description
                  </label>
                  <input
                    type="text"
                    id="description"
                    name="description"
                    required
                    className="mt-1 block w-full border-grey-300 rounded-md shadow-sm focus:ring-maroon-500 focus:border-maroon-500 sm:text-sm"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="e.g. Standard Dental Cleaning"
                  />
                </div>

                <div>
                  <label htmlFor="default_cost" className="block text-sm font-medium text-grey-700">
                    Default Cost
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiDollarSign} className="h-5 w-5 text-grey-400" />
                    </div>
                    <input
                      type="number"
                      id="default_cost"
                      name="default_cost"
                      min="0"
                      step="0.01"
                      required
                      className="focus:ring-maroon-500 focus:border-maroon-500 block w-full pl-10 sm:text-sm border-grey-300 rounded-md"
                      value={formData.default_cost}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="flex items-start mt-4">
                  <div className="flex items-center h-5">
                    <input
                      id="is_specialist"
                      name="is_specialist"
                      type="checkbox"
                      className="focus:ring-maroon-500 h-4 w-4 text-maroon-600 border-grey-300 rounded"
                      checked={formData.is_specialist}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="is_specialist" className="font-medium text-grey-700">
                      Specialist Treatment
                    </label>
                    <p className="text-grey-500">
                      Specialist treatments use the specialist payout percentage for doctors.
                    </p>
                  </div>
                </div>

                {editingCode && (
                  <div className="flex items-start mt-4">
                    <div className="flex items-center h-5">
                      <input
                        id="active"
                        name="active"
                        type="checkbox"
                        className="focus:ring-maroon-500 h-4 w-4 text-maroon-600 border-grey-300 rounded"
                        checked={formData.active}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="active" className="font-medium text-grey-700">
                        Active
                      </label>
                      <p className="text-grey-500">
                        Inactive codes won't appear in treatment selection dropdowns.
                      </p>
                    </div>
                  </div>
                )}

                <div className="pt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="mr-3 px-4 py-2 border border-grey-300 shadow-sm text-sm font-medium rounded-md text-grey-700 bg-white hover:bg-grey-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-maroon-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-maroon-600 hover:bg-maroon-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-maroon-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                        {editingCode ? 'Update Code' : 'Create Code'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default TreatmentCodes;